import React, { useContext, useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { FINISHES_NEGATIVE, FINISHES_POSITIVE, FINISHES_EVEN } from '../data/const';
import { getSwingSizes } from '../data/player_insights';
import { DS, Meter, Overline, Panel, formatSigned, numericSx } from '../shared/Dashboard';

const RoundOutcomePercentages = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { summaryData, historyData } = context || {};

    const swings = useMemo(() => getSwingSizes(historyData ?? []), [historyData]);

    if (isEmpty(summaryData)) return null;

    const rc = summaryData.grouped_rating_changes;
    const outcomes = [
        { label: 'Gained', color: DS.gain, percent: rc[FINISHES_POSITIVE]?.percent ?? 0, count: rc[FINISHES_POSITIVE]?.total ?? 0 },
        { label: 'Even', color: DS.neutral, percent: rc[FINISHES_EVEN]?.percent ?? 0, count: rc[FINISHES_EVEN]?.total ?? 0 },
        { label: 'Lost', color: DS.loss, percent: rc[FINISHES_NEGATIVE]?.percent ?? 0, count: rc[FINISHES_NEGATIVE]?.total ?? 0 },
    ];
    const bounceBack: number = summaryData.bounce_back_percentage ?? 0;

    return (
        <Panel title="Rating outcomes" subtitle="How often a round moves your rating" grid={{ xs: 12, md: 6 }} delay={0.05}>
            {/* Segmented distribution */}
            <Box sx={{ display: 'flex', height: 12, borderRadius: '999px', overflow: 'hidden', gap: '2px', background: DS.inset }}>
                {outcomes.filter(o => o.percent > 0).map(o => (
                    <Tooltip key={o.label} title={`${o.label}: ${o.count} rounds`} arrow>
                        <Box sx={{ width: `${o.percent}%`, background: o.color }} />
                    </Tooltip>
                ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
                {outcomes.map(o => (
                    <Box key={o.label}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: o.color }} />
                            <Overline>{o.label}</Overline>
                        </Box>
                        <Typography sx={{ fontSize: '28px', fontWeight: 800, color: DS.text, lineHeight: 1.2, ...numericSx }}>
                            {o.percent}%
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{o.count} rounds</Typography>
                    </Box>
                ))}
            </Box>

            {/* Swing size */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 3, pt: 2.5, borderTop: `1px solid ${DS.border}` }}>
                <Box>
                    <Overline>Avg gain</Overline>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: DS.gain, ...numericSx }}>{formatSigned(swings.averageGain, 1)}</Typography>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>per winning round</Typography>
                </Box>
                <Box>
                    <Overline>Avg loss</Overline>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: DS.loss, ...numericSx }}>{formatSigned(swings.averageLoss, 1)}</Typography>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>per losing round</Typography>
                </Box>
            </Box>

            {/* Bounce back */}
            <Box sx={{ mt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                    <Overline>Bounce-back rate</Overline>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: DS.text, ...numericSx }}>{bounceBack}%</Typography>
                </Box>
                <Meter value={bounceBack / 100} color={DS.info} />
                <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.75 }}>
                    How often you avoid losing rating again right after a losing round.
                </Typography>
            </Box>
        </Panel>
    );
};

export default RoundOutcomePercentages;
